'use client';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import { useIsSp } from '@/hooks/useMediaQuery';
import { sendPickupBannerClickEvent } from '@/utils/dualTrackEvents';
import type { Route } from 'next';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import type { BannerResponse } from '../../types';
import styles from './WorkBannerList.module.scss';

const splideOptions = {
  type: 'loop',
  arrows: false,
  pagination: false,
  // デザイン通り、1 枚目を画面幅の 57% に固定し、右側に 2 枚目を覗かせる。
  // 残り 43% に 2 枚目のカード幅の 75% (43 / 57 ≒ 0.75) 相当が顔を出すように
  fixedWidth: '57%',
  gap: '0.5rem',
  autoplay: true,
  pauseOnHover: false,
  speed: 500,
  interval: 5000,
};

// 自サイトの origin と一致するか相対パスなら内部リンクとみなし、pathname を返す
// 外部URL・不正URLは null
function resolveInternalPath(url: string): string | null {
  if (url.startsWith('/')) return url;
  try {
    const parsed = new URL(url);
    const base = process.env.NEXT_PUBLIC_BASE_URL;
    if (base != null && parsed.origin === new URL(base).origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // 不正URLは外部扱い
  }
  return null;
}

function BannerCard({
  banner,
  position,
  onClick,
}: {
  banner: BannerResponse;
  position: number;
  onClick: (bannerId: number, position: number) => void;
}) {
  const image = (
    <Image
      src={banner.imageUrl}
      alt=''
      width={1600}
      height={760}
      sizes='(min-width: 769px) 33vw, 80vw'
      className={styles.image}
    />
  );

  const internalPath = resolveInternalPath(banner.url);
  if (internalPath != null) {
    return (
      <Link href={internalPath as Route} className={styles.link} onClick={() => onClick(banner.id, position)}>
        {image}
      </Link>
    );
  }

  return (
    <a
      href={banner.url}
      target='_blank'
      rel='noopener noreferrer'
      className={styles.link}
      onClick={() => onClick(banner.id, position)}
    >
      {image}
    </a>
  );
}

function WorkBannerList({ banners }: { banners: BannerResponse[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const isSp = useIsSp();
  const session = useSession();

  const handleBannerClick = (bannerId: number, position: number) => {
    sendPickupBannerClickEvent({ bannerId, position, userId: session?.data?.user.id ?? 0 });
  };

  if (banners.length === 0) return null;

  if (!isSp) {
    return (
      <ul className={styles.list}>
        {banners.map((banner, index) => (
          <li key={banner.id} className={styles.item}>
            <BannerCard banner={banner} position={index} onClick={handleBannerClick} />
          </li>
        ))}
      </ul>
    );
  }

  // 1 件のときは Splide を使わず単一表示にして、loop の切れ端や pagination が出ないようにする
  if (banners.length === 1) {
    return (
      <div className={styles.singleContainer}>
        <BannerCard banner={banners[0]} position={0} onClick={handleBannerClick} />
      </div>
    );
  }

  return (
    <div className={styles.sliderContainer}>
      <Splide
        options={splideOptions}
        onMove={({ index }) => setActiveIndex(index)}
        aria-label='作品バナー'
        hasTrack={false}
      >
        <SplideTrack>
          {banners.map((banner, index) => (
            <SplideSlide key={banner.id}>
              <BannerCard banner={banner} position={index} onClick={handleBannerClick} />
            </SplideSlide>
          ))}
        </SplideTrack>
      </Splide>
      <div className={styles.pagination} aria-hidden='true'>
        {banners.map((banner, index) => (
          <span key={`dot_${banner.id}`} className={`${styles.dot} ${activeIndex === index ? styles.dotActive : ''}`} />
        ))}
      </div>
    </div>
  );
}

export default WorkBannerList;
